import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Wrench, 
  Bug, 
  Shield, 
  AlertTriangle, 
  Archive, 
  Trash2 
} from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

type ChangelogEntryType = Database['public']['Tables']['changelog_entries']['Row'];

interface ChangelogEntryProps {
  entry: ChangelogEntryType;
}

const ChangelogEntry = ({ entry }: ChangelogEntryProps) => {
  const { t } = useTranslation('changelog');

  const getEntryTypeConfig = (type: ChangelogEntryType['entry_type']) => {
    switch (type) {
      case 'feature':
        return {
          icon: Plus,
          color: 'bg-green-100 text-green-800 border-green-200',
          label: t('entryTypes.feature')
        };
      case 'improvement':
        return {
          icon: Wrench,
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          label: t('entryTypes.improvement')
        };
      case 'bugfix':
        return {
          icon: Bug,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: t('entryTypes.bugfix')
        };
      case 'security':
        return {
          icon: Shield,
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          label: t('entryTypes.security')
        };
      case 'breaking':
        return {
          icon: AlertTriangle,
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          label: t('entryTypes.breaking')
        };
      case 'deprecated':
        return {
          icon: Archive,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          label: t('entryTypes.deprecated')
        };
      case 'removed':
        return {
          icon: Trash2,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: t('entryTypes.removed')
        };
      default:
        return {
          icon: Plus,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          label: t('entryTypes.default')
        };
    }
  };

  const config = getEntryTypeConfig(entry.entry_type);
  const IconComponent = config.icon;

  return (
    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
      <div className={`p-1.5 rounded-full ${config.color} flex-shrink-0`}>
        <IconComponent className="w-3 h-3" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-foreground">{entry.title}</h4>
          <Badge variant="outline" className={`text-xs ${config.color}`}>
            {config.label}
          </Badge>
          {entry.breaking_change && (
            <Badge variant="destructive" className="text-xs">
              {t('version.breaking')}
            </Badge>
          )}
          {entry.migration_required && (
            <Badge variant="secondary" className="text-xs">
              {t('version.migrationRequired')}
            </Badge>
          )}
        </div>
        {entry.description && (
          <p className="text-sm text-muted-foreground mb-2">{entry.description}</p>
        )}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {entry.component_affected && (
            <span>{t('metadata.component')} <code className="bg-muted px-1 py-0.5 rounded">{entry.component_affected}</code></span>
          )}
          {entry.page_affected && (
            <span>{t('metadata.page')} <code className="bg-muted px-1 py-0.5 rounded">{entry.page_affected}</code></span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogEntry;